package com.tregz.tremenz.ui.page

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import com.tregz.tremenz.ui.component.Title
import com.tregz.tremenz.ui.component.EmptyState
import com.tregz.tremenz.ui.label.LabelKey
import com.tregz.tremenz.ui.label.labelText

/**
 * A dated announcement. Phase 5 backs [PageNews] with the Firestore collection that already feeds
 * the static site; the rendering below does not change when it does.
 */
data class PageNewsEntry(
    val date: String,
    val title: String,
    val body: String
)

val PAGE_NEWS_ENTRIES: List<PageNewsEntry> = listOf(
    PageNewsEntry(
        date = "2026",
        title = "New 2026 design",
        body = "A brand-new design for 2026, completely rebuilt with modern multiplatform technology. Enjoy a smoother, responsive layout for streaming continuous video and audio mixes across mobile and web. Please let me know if you notice any bug or error. Your support is tremendously appreciated."
    ),
    PageNewsEntry(
        date = "2021",
        title = "New Android app",
        body = "Video mixtapes are now available on Android. With the app you may stream to your tv with Chromecast. If you liked the facebook fanpage, you should get notifications for updates.\n\nPlease let me know if you notice any bug or error. Your support is tremendously appreciated."
    ),
    PageNewsEntry(
        date = "2017",
        title = "New website design",
        body = "I'm looking forward to read your feedback for this faster and more user-friendly navigation. There is still so much more to be added. If you liked the facebook fanpage, you should get notifications for updates.\n\nPlease let me know if you notice any bug or error. Your support is tremendously appreciated."
    ),
    PageNewsEntry(
        date = "2016",
        title = "As seen on Trace TV",
        body = "Thanks to Vocalteknix for selecting some of my video edits for his weekly video mix broadcasted on Trace Urban."
    )
)

@Composable
fun pageNewsEntries(): List<PageNewsEntry> = listOf(
    PageNewsEntry(
        date = "2026",
        title = labelText(LabelKey.NEWS_2026_TITLE),
        body = labelText(LabelKey.NEWS_2026_BODY)
    ),
    PageNewsEntry(
        date = "2021",
        title = labelText(LabelKey.NEWS_2021_TITLE),
        body = labelText(LabelKey.NEWS_2021_BODY)
    ),
    PageNewsEntry(
        date = "2017",
        title = labelText(LabelKey.NEWS_2017_TITLE),
        body = labelText(LabelKey.NEWS_2017_BODY)
    ),
    PageNewsEntry(
        date = "2016",
        title = labelText(LabelKey.NEWS_2016_TITLE),
        body = labelText(LabelKey.NEWS_2016_BODY)
    )
)

/** Web exclusive destination carrying what the static site published under "News". */
@Composable
fun PageNews(
    entries: List<PageNewsEntry> = pageNewsEntries(),
    modifier: Modifier = Modifier
) {
    val scheme = MaterialTheme.colorScheme

    LazyColumn(
        modifier = modifier.fillMaxSize(),
        contentPadding = PaddingValues(bottom = 24.dp)
    ) {
        item {
            Title(
                title = labelText(LabelKey.ROUTE_NEWS),
                subtitle = labelText(LabelKey.HEADING_NEWS_SUBTITLE)
            )
        }

        if (entries.isEmpty()) {
            item {
                EmptyState(message = labelText(LabelKey.EMPTY_NEWS))
            }
        } else {
            items(entries) { entry ->
                Card(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = 16.dp, vertical = 6.dp),
                    colors = CardDefaults.cardColors(containerColor = scheme.surface),
                    shape = MaterialTheme.shapes.medium
                ) {
                    Column(modifier = Modifier.padding(16.dp)) {
                        Text(
                            text = entry.date,
                            color = scheme.primary,
                            style = MaterialTheme.typography.labelMedium
                        )
                        Spacer(modifier = Modifier.height(4.dp))
                        Text(
                            text = entry.title,
                            color = scheme.onSurface,
                            style = MaterialTheme.typography.titleMedium
                        )
                        Spacer(modifier = Modifier.height(8.dp))
                        HorizontalDivider(color = scheme.outline)
                        Spacer(modifier = Modifier.height(8.dp))
                        Text(
                            text = entry.body,
                            color = scheme.onSurfaceVariant,
                            style = MaterialTheme.typography.bodyMedium
                        )
                    }
                }
            }
        }
    }
}
